import { FC } from "react";
import { useSnackbar, VariantType } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { logActivity } from "@/api";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";

import DefaultInquiryForm from "./DefaultInquiryForm";
import { updateInquiry } from "../inquiries.api";
import { useCustomFields } from "../hooks/useCustomFields";
import {
  processEditCustomAttributesForAPI,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";
import type {
  EditInquiryFormData,
  Inquiry,
  InquiryFormData,
} from "../inquiries.types";

interface EditInquiryFormProps {
  width?: number;
  defaultFormData: Inquiry;
  onSubmit: (result?: unknown) => void;
  onCancel: () => void;
}

const EditInquiryForm: FC<EditInquiryFormProps> = ({
  width,
  defaultFormData,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: customFields = [] } = useCustomFields();

  const handleSubmit = async (
    _data: InquiryFormData,
    diffData?: EditInquiryFormData
  ) => {
    try {
      const cleanedDiff = extractCustomFieldsFromFormData(
        (diffData || {}) as Record<string, unknown>
      );

      const customAttributes = processEditCustomAttributesForAPI(
        (diffData || {}) as Record<string, unknown>,
        customFields,
        defaultFormData.inquiry_id,
        defaultFormData.custom_attributes || []
      );

      const updateData = {
        ...cleanedDiff,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      if (Object.keys(updateData).length === 0) {
        enqueueSnackbar("No changes to save.", {
          variant: "info" as VariantType,
        });
        onCancel?.();
        return;
      }

      const result = await updateInquiry(
        defaultFormData.inquiry_id,
        updateData
      );

      enqueueSnackbar("Inquiry updated successfully.", {
        variant: "success" as VariantType,
      });

      const diffKeys = Object.keys(diffData || {});
      const onlyCustomFields =
        diffKeys.length > 0 &&
        diffKeys.every((k) => k.startsWith("custom_attribute_"));

      logActivity(
        "Admin",
        currentUser.user_id,
        onlyCustomFields
          ? `Edited Inquiry Custom Fields ${defaultFormData.inquiry_id}`
          : `Edited Inquiry Details ${defaultFormData.inquiry_id}`
      );

      queryClient.invalidateQueries({ queryKey: ["inquiries:list"] });
      queryClient.invalidateQueries({
        queryKey: ["inquiry", { inquiry_id: defaultFormData.inquiry_id }],
      });

      onSubmit?.(result);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while updating this inquiry. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Edit Inquiry</FormTitle>
      <DefaultInquiryForm
        mode="edit"
        defaultFormData={defaultFormData}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};

export default EditInquiryForm;
